/**
 * Resolving a human-readable name for the signed-in user.
 *
 * Several screens used to hardcode a developer's name ("Ramakrishna",
 * "Rama Krishna Yadla") as the default/fallback, which shipped that name to
 * every user whose profile hadn't loaded yet. Route those through here instead:
 * real name → email local-part → a neutral generic, never someone else's name.
 */

type NamedUser = {
  name?: string | null
  email?: string | null
}

/** Generic used for labels/nouns ("User"). Greetings pass "there". */
export const DEFAULT_DISPLAY_NAME = 'User'

/**
 * `name` → local-part of `email` → `fallback`.
 *
 * @param fallback shown only when the user has neither a name nor an email
 *                 (guest join, pre-login). Use "there" for greetings.
 */
export function userDisplayName(
  user: NamedUser | null | undefined,
  fallback: string = DEFAULT_DISPLAY_NAME,
): string {
  const name = user?.name?.trim()
  if (name) return name

  const local = user?.email?.trim().split('@')[0]?.trim()
  if (local) return local

  return fallback
}

/** `@handle` form of the display name — lowercased, non-word chars stripped. */
export function userHandle(
  user: NamedUser | null | undefined,
  fallback: string = DEFAULT_DISPLAY_NAME,
): string {
  return userDisplayName(user, fallback).toLowerCase().replace(/[^a-z0-9._-]/g, '')
}
